import {
  Banner,
  Box,
  Button,
  Divider,
  DropdownButtonContext,
  FlexBox,
  InputSelectionList,
  InputSelectionListChangeEvent,
  SelectionList,
} from '@dixa/lib-shared-habla';
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { aiAgentsApi } from 'apis/aiAgents/aiAgents.api';
import { useRunAiAgent } from 'apis/aiAgents/useRunAiAgent';
import { AiLoadingState } from 'components/aiLoadingState';
import { CopilotDropdownActionContext } from 'components/copilotDropdown';
import { HtmlRenderer } from 'components/htmlRenderer';
import { sanitizationProfile, useEditorContext } from 'utils/useEditorContext';
import { useCurrentUser } from 'utils/user/useCurrentUser';

import { CopilotDropdownHeader } from '../../../copilotDropdown/components';
import { EditorCopilotDropdownContentBox } from '../contentBox';

interface AiDraftContentProps {
  dixaConversationId: string;
  initialDraft?: string;
  initialAgentId?: string;
  onDraftGenerated: (text: string | undefined) => void;
  onAgentSelected: (agentId: string) => void;
}

export const AiDraftContent = memo(function AiDraftContent({
  dixaConversationId,
  initialDraft,
  initialAgentId,
  onDraftGenerated,
  onAgentSelected,
}: AiDraftContentProps) {
  const { user } = useCurrentUser();
  const {
    data,
    isLoading: isLoadingAgents,
    isError: isAgentsError,
  } = aiAgentsApi.useReadAiAgentsListQuery({});
  const agents = (data?.data?.agents ?? [])
    .filter((agent) => agent.variant !== 'automationbot')
    .reverse();
  const noAgents = !isLoadingAgents && agents.length === 0;

  const { onBack } = useContext(CopilotDropdownActionContext);
  const { insertBelow } = useEditorContext();
  const dropdownContext = useContext(DropdownButtonContext);

  const [selectedAgentId, setSelectedAgentId] = useState<string | undefined>(
    initialAgentId,
  );
  const selectedAgent = agents.find(
    (a) => a.id === (selectedAgentId ?? agents[0]?.id),
  );

  const {
    run,
    text,
    isLoading,
    isError,
    generatedByAgentId: liveAgentId,
  } = useRunAiAgent(selectedAgent?.id ?? '', dixaConversationId, user.id);

  // Persist draft across panel open/close, tied to the agent that generated it
  const [displayedText, setDisplayedText] = useState<string | undefined>(
    initialDraft,
  );
  const [generatedByAgentId, setGeneratedByAgentId] = useState<
    string | undefined
  >(initialAgentId);

  // Prefer live streaming text; fall back to locally persisted draft (survives agent switches)
  const activeDisplayedText =
    liveAgentId === selectedAgent?.id && text
      ? text
      : generatedByAgentId === selectedAgent?.id
        ? displayedText
        : undefined;

  useEffect(() => {
    if (text && liveAgentId) {
      setDisplayedText(text);
      setGeneratedByAgentId(liveAgentId);
      onDraftGenerated(text);
    }
  }, [text, liveAgentId, onDraftGenerated]);

  const handleAgentChange = useCallback(
    (event: InputSelectionListChangeEvent<string>) => {
      const agentId = event.selectedItem?.value;
      if (!agentId) return;
      setSelectedAgentId(agentId);
      onAgentSelected(agentId);
    },
    [onAgentSelected],
  );

  const canUseDraft = !!activeDisplayedText && !isLoading;

  const content = useMemo(() => {
    if (isAgentsError) {
      return (
        <Banner
          variant="fail"
          description={<span>Could not load agents. Please try again.</span>}
        />
      );
    }
    if (noAgents) {
      return (
        <Banner
          variant="info"
          description={<span>No agents configured.</span>}
        />
      );
    }
    if (activeDisplayedText) {
      return (
        <HtmlRenderer
          sanitizationProfileType={sanitizationProfile}
          html={activeDisplayedText}
        />
      );
    }
    if (isLoading) {
      return <AiLoadingState label="" />;
    }
    if (isError) {
      return (
        <Banner
          variant="fail"
          description={
            <span>An error occurred while generating the reply.</span>
          }
        />
      );
    }
    return null;
  }, [isAgentsError, noAgents, isLoading, isError, activeDisplayedText]);

  const handleButtonClick = useCallback(() => {
    if (canUseDraft) {
      insertBelow(activeDisplayedText!);
      setDisplayedText(undefined);
      setGeneratedByAgentId(undefined);
      onDraftGenerated(undefined);
      dropdownContext.closeDropdown();
    } else {
      run();
    }
  }, [
    canUseDraft,
    activeDisplayedText,
    insertBelow,
    onDraftGenerated,
    dropdownContext,
    run,
  ]);

  const agentPicker = noAgents ? null : (
    <InputSelectionList<string>
      value={selectedAgent ? [selectedAgent.id] : []}
      getItemTitleByValue={(id) =>
        agents.find((a) => a.id === id)?.name ?? id ?? ''
      }
      placeholder={isLoadingAgents ? 'Loading agents...' : 'Select an AI agent'}
      onChange={handleAgentChange}
      style={{ maxWidth: 320 }}
      disabled={isLoadingAgents}
    >
      {agents.map((agent) => (
        <SelectionList.Item key={agent.id} value={agent.id}>
          {agent.name ?? agent.id}
        </SelectionList.Item>
      ))}
    </InputSelectionList>
  );

  return (
    <Box style={{ maxWidth: 768, minWidth: 540 }}>
      <CopilotDropdownHeader title="AI agent draft" onBack={onBack}>
        {agentPicker}
      </CopilotDropdownHeader>

      {(isAgentsError ||
        noAgents ||
        isLoading ||
        isError ||
        !!activeDisplayedText) && (
        <EditorCopilotDropdownContentBox
          isEmphasized={!!activeDisplayedText}
          showUndo={false}
        >
          {content}
        </EditorCopilotDropdownContentBox>
      )}

      <Divider />

      <FlexBox padding="250" justifyContent="flex-end">
        <Button
          variant="primary"
          disabled={isLoadingAgents || isAgentsError || noAgents || isLoading}
          onClick={handleButtonClick}
        >
          {canUseDraft ? 'Use draft' : 'Generate draft'}
        </Button>
      </FlexBox>
    </Box>
  );
});
